import { createContext, useContext } from 'react';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | null;
export type SubscriptionStatus = 'pending_approval' | 'trial' | 'active' | 'cancelled' | 'paused' | null;

export interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
  company_name: string | null;
  approval_status: ApprovalStatus;
}

export interface SubscriptionPlan {
  id: string;
  key: string;
  name: string;
  description: string | null;
  price_usd_monthly: number;
  price_addon_usd_monthly?: number;
  included_panels: number;
  included_features: string[];
  granted_panel_categories?: string[];
  is_enterprise: boolean;
  active: boolean;
  sort_order: number;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: SubscriptionStatus;
  extra_panels_count: number;
  current_period_end: string | null;
}

export interface PanelAccessRow {
  id: string;
  panel_id: string | null;
  brand_id: string | null;
  access_type: 'panel' | 'brand_full';
}

export interface EntitlementContextType {
  loading: boolean;
  profile: Profile | null;
  approvalStatus: ApprovalStatus;
  /** @deprecated kept for backwards compatibility — first active membership */
  currentPlan: SubscriptionPlan | null;
  /** All active plan memberships for this user */
  memberships: SubscriptionPlan[];
  subscription: UserSubscription | null;
  effectiveFeatures: string[];
  effectivePanelCategories: string[];
  panelAccess: PanelAccessRow[];
  hasFeature: (key: string) => boolean;
  hasPanelAccess: (panelId: string) => boolean;
  hasPanelCategoryAccess: (category: string | null | undefined) => boolean;
  isEnterprise: boolean;
  isBeta: boolean;
  refresh: () => Promise<void>;
}

export const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined);

export const useEntitlements = () => {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error('useEntitlements must be used within an EntitlementProvider');
  return ctx;
};
